import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useMemo, useState, useEffect } from "react";
import { useFragment } from "react-relay";

import { setFiltersFromStorage } from "@/components/AgendaFilters/utils/setFiltersFromStorage";
import { setLastSelectedBookingSelection } from "@/utils/bookingSelectionStorage";
import { CheckBox } from "@whoosh/design-system/src/components/controls";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { TeeSheetCourseSelectorControlFragment_facility$key } from "./__generated__/TeeSheetCourseSelectorControlFragment_facility.graphql";

type Props = {
  facilityRef: TeeSheetCourseSelectorControlFragment_facility$key | null;
  onChange: (courseIds: string[]) => void;
  courseIds: string[];
};

export const TeeSheetCourseSelectorControl = ({
  facilityRef,
  courseIds,
  onChange,
}: Props) => {
  const facility = useFragment(
    TeeSheetCourseSelectorControlFragmentString,
    facilityRef
  );

  const allCourseIds = useMemo(
    () => facility?.courses?.edges?.map((c) => c?.node?.id ?? "") || [],
    [facility?.courses?.edges]
  );

  const [currentCourseIds, setCurrentCourseIds] = useState<string[]>(courseIds);
  const allCoursesSelected = currentCourseIds?.length === allCourseIds.length;

  useEffect(() => {
    setCurrentCourseIds(courseIds);
  }, [courseIds]);

  const shouldShowCourseSelector =
    facility?.courses?.edges && facility?.courses?.edges?.length > 1;

  return shouldShowCourseSelector ? (
    <VStack>
      <Text py={2} mb={4} variant="mainSmallSemiBold">
        {t`Courses`}
      </Text>
      <Box mb={4}>
        <CheckBox
          ariaLabel={t`All`}
          isChecked={allCoursesSelected}
          onPress={() => {
            setCurrentCourseIds(allCoursesSelected ? [] : allCourseIds);
            onChange(allCoursesSelected ? [] : allCourseIds);
            if (facility?.id) {
              setFiltersFromStorage(
                `x-whoosh-${facility.id}-course-ids`,
                allCoursesSelected ? "" : allCourseIds.join(",")
              );
              setLastSelectedBookingSelection({ facilityId: facility.id });
            }
          }}
          label={t`All`}
          testID="course-filters-toggle-all"
        />
      </Box>
      <>
        {facility?.courses?.edges?.map((course) => {
          const isChecked = currentCourseIds.includes(course?.node?.id ?? "");
          const newCourseIds = isChecked
            ? currentCourseIds.filter((id) => id !== course?.node?.id)
            : [...currentCourseIds, course?.node?.id ?? ""];
          return (
            <CheckBox
              ariaLabel={course?.node?.name}
              isChecked={isChecked}
              onPress={() => {
                setCurrentCourseIds(newCourseIds);
                onChange(newCourseIds);
                setFiltersFromStorage(
                  `x-whoosh-${facility?.id}-course-ids`,
                  newCourseIds.join(",")
                );
                if (facility?.id) {
                  setLastSelectedBookingSelection({
                    facilityId: facility.id,
                    courseId:
                      newCourseIds.length === 1 ? newCourseIds[0] : null,
                  });
                }
              }}
              testID={`course-filters-toggle-${course?.node?.name}`}
              label={course?.node?.name}
            />
          );
        })}
      </>
    </VStack>
  ) : null;
};

export const TeeSheetCourseSelectorControlFragmentString = graphql`
  fragment TeeSheetCourseSelectorControlFragment_facility on Facility {
    id
    name
    slug
    multipleBookingsEnabled
    courses {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;
